import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@veeva/corgix/button';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import Dialog from './Dialog';
import useMessageService from '../services/i18n/useMessageService';

const propTypes = {
    /**
     * If true, shows an unsaved changes dialog when navigating to a new url or a browser alert if leaving the website.
     */
    when: PropTypes.bool,
};

function BlockingNavigationDialog({ when }) {
    const unblockRef = useRef();
    const history = useHistory();
    const [
        [
            stayLabel = 'Stay',
            leaveLabel = 'Leave',
            unsavedChangesDialogTitle = 'You Have Unsaved Changes',
            unsavedChangesDialogBody = 'Are you sure you want to leave this page?',
        ],
    ] = useMessageService([
        'my_account.unsaved_changes_dialog_stay',
        'my_account.unsaved_changes_dialog_leave',
        'my_account.unsaved_changes_dialog_title',
        'my_account.unsaved_changes_dialog_body',
    ]);
    const [currentHistoryPath, setCurrentHistoryPath] = useState('');
    const [showBlockingNavigationDialog, setShowBlockingNavigationDialog] = useState(false);

    const showBrowserUnsavedChangesDialog = (e) => {
        e.preventDefault();
        e.returnValue = '';
        return '';
    };

    // eslint-disable-next-line consistent-return
    useEffect(() => {
        if (when) {
            // Shows the default browser dialog if they navigate to a new site or close the tab (can't be overridden).
            window.addEventListener('beforeunload', showBrowserUnsavedChangesDialog);

            // Shows a custom "unsaved changes" dialog if navigating to a page within VeevaID.
            unblockRef.current = history.block((location) => {
                setShowBlockingNavigationDialog(true);
                setCurrentHistoryPath(location.pathname);

                return false;
            });

            return () => {
                unblockRef.current?.();
                window.removeEventListener('beforeunload', showBrowserUnsavedChangesDialog);
            };
        }
    }, [history, when]);

    const handleLeaveClick = () => {
        setShowBlockingNavigationDialog(false);
        unblockRef.current?.();
        history.push(currentHistoryPath);
    };
    const handleStayClick = () => {
        setShowBlockingNavigationDialog(false);
        setCurrentHistoryPath('');
    };

    const actions = [
        <Button
            css={({ blue }) =>
                css`
                    color: ${blue};

                    &:not([disabled]):hover,
                    &:not([disabled]):focus,
                    &:not([disabled]):active {
                        color: ${blue};
                    }
                `
            }
            key="stay"
            variant="ghost"
            onClick={handleStayClick}
        >
            {stayLabel}
        </Button>,
        <Button key="leave" variant="primary" onClick={handleLeaveClick}>
            {leaveLabel}
        </Button>,
    ];

    return (
        <Dialog
            title={unsavedChangesDialogTitle}
            actions={actions}
            open={showBlockingNavigationDialog}
        >
            {unsavedChangesDialogBody}
        </Dialog>
    );
}

BlockingNavigationDialog.propTypes = propTypes;

export default BlockingNavigationDialog;
